<template>
  <GenericCard
    :locked="locked"
    has-delete
    :on-delete="onCardDelete"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="v$.section.$invalid"
    :dirty="v$.section.$anyDirty"
    :create="create"
    title="Declaration"
    :section="section"
    has-footer
    has-share
    :card-identifier="cardIdentifier"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <v-row>
        <v-col cols="12">
          <v-alert
            border="left"
            colored-border
            color="#2A3B4D"
            icon="mdi-information-outline"
            elevation="2"
            class="mb-6"
          >
            All I-589 part B content is referenced in an Attachment of Declaration. Add below the narrative for an I-589 attachment or a stand-alone declaration for an I-589.
          </v-alert>
        </v-col>
        <v-col cols="12">
          <v-radio-group
            v-model="section.kind"
            row
          >
            <v-radio
              label="Declaration"
              value="declaration"
            />
            <v-radio
              label="Attachment"
              value="attachment"
            />
          </v-radio-group>
        </v-col>
        <v-col cols="12">
          <v-text-field
            v-model="section.interpreter_full_name"
            outlined
            autocomplete="off"
            label="Interpreter Full Name"
          />
        </v-col>
        <v-col cols="12">
          <v-text-field
            v-model="section.source_language"
            outlined
            autocomplete="off"
            label="Source Language"
          />
        </v-col>
        <v-col cols="12">
          <v-textarea
            v-model="section.narrative"
            auto-grow
            outlined
            autocomplete="off"
            label="Narrative"
          />
        </v-col>
      </v-row>

      <v-row
        v-if="locked"
        key="2"
      >
        <v-col
          key="2-0"
          class="mt-5 text-right"
          cols="12"
        >
          <v-btn
            color="primary"
            :disabled="!locked"
            @click="onGeneratePDF"
          >
            Generate Declaration
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { useVuelidate } from '@vuelidate/core'

import { PARTY_NAMESPACE } from '@/store/party'
import { alertHandler } from '@/alertHandler'
import { downloadURI } from '@/utilities'

import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard'

export default {
  name: 'DeclarationCard',
  components: { GenericCard },

  props: {
    item: {
      type: Object,
      required: true,
    },
  },

  setup () {
    return {
      v$: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      section: {},
    }
  },

  validations () {
    return {
      section: {},
    }
  },

  computed: {
    cardIdentifier () { return `declaration-card-${this.sectionIdentifier}` },
    create () { return this.item.id === null },

    locked: {
      get () {
        return this.peDeclarationCardLock(this.sectionIdentifier)
      },

      set (value) {
        this.setPeDeclarationCardLock({ index: this.sectionIdentifier, locked: value })
      },
    },

    sectionIdentifier () { return this.section.id || 'new' },

    subtitle () {
      if (this.create) return ''
      return `(${this.section.name})`
    },

    ...mapGetters(PARTY_NAMESPACE, [
      'peDeclarationCardLock',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
      },

      deep: true,
    },
  },

  created () {
    if (!this.create) this.initPeDeclarationCard(this.sectionIdentifier)
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onCardCreate () {
      return this.peDeclarationCardCreate({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardDelete () {
      if (this.create) return this.peDeclarationCardCreateUndo()
      else {
        return this.peDeclarationCardDelete({ id: this.section.id, index: this.sectionIdentifier })
      }
    },

    async onCardRefresh () {
      return this.peDeclarationCardRefresh({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardUpdate () {
      return this.peDeclarationCardUpdate({ id: this.section.id, index: this.sectionIdentifier, section: this.section })
    },

    async onGeneratePDF () {
      const pdfData = await this.peDeclarationCardDownloadPDF()
      if (!pdfData.pdf) {
        this.displayAlert('error', 'Unable to generate PDF')
      } else {
        this.displayAlert('success', 'Generated PDF successfully, downloading...')
        const data = `data:application/pdf;base64,${pdfData.pdf}`
        downloadURI(data, pdfData.pdf_filename)
      }
    },

    async onSectionUnlock () {
      return this.setPeDeclarationCardLock({ index: this.sectionIdentifier, locked: false })
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else if (this.create) return this.onCardCreate()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'peDeclarationCardCreateUndo',
      'setPeDeclarationCardLock',

    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'initPeDeclarationCard',
      'peDeclarationCardCreate',
      'peDeclarationCardDelete',
      'peDeclarationCardDownloadPDF',
      'peDeclarationCardRefresh',
      'peDeclarationCardUpdate',
    ]),
  },
}
</script>
